import { useState, useMemo } from "react";
import { Search, CheckCircle2, Circle, ArrowUpDown, Calendar, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { formatDistanceToNow, format } from "date-fns";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import type { Board, Card } from "@/types/board";
import { EditCardDialog } from "./board/EditCardDialog";

interface CheatsheetProps {
    boards: Board[];
    onUpdate: () => void;
}

type SortField = 'title' | 'boardTitle' | 'columnTitle' | 'created_at' | 'due_date';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
    field: SortField;
    direction: SortDirection;
}

export function Cheatsheet({ boards, onUpdate }: CheatsheetProps) {
    const { toast } = useToast();
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedBoardId, setSelectedBoardId] = useState<string>("");
    const [newTaskTitle, setNewTaskTitle] = useState("");
    const [isAdding, setIsAdding] = useState(false);

    // Sorting state
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        field: 'created_at',
        direction: 'desc'
    });

    // Dialog state
    const [selectedCard, setSelectedCard] = useState<Card | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [hideDone, setHideDone] = useState(false);

    // Flatten all cards from all boards into a single list
    const allCards = useMemo(() => {
        const cards: (Card & { boardTitle: string; boardId: string; columnTitle: string })[] = [];
        boards.forEach((board) => {
            board.columns?.forEach((column) => {
                column.cards?.forEach((card) => {
                    cards.push({
                        ...card,
                        boardTitle: board.title,
                        boardId: board.id,
                        columnTitle: column.title,
                    });
                });
            });
        });
        return cards;
    }, [boards]);

    const sortedAndFilteredCards = useMemo(() => {
        let result = allCards.filter((card) =>
            (card.title?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
            card.boardTitle.toLowerCase().includes(searchQuery.toLowerCase())
        );

        // Filter out done tasks if hideDone is enabled
        if (hideDone) {
            result = result.filter((card) => {
                const status = card.columnTitle.toLowerCase();
                return !status.includes('done') && !status.includes('complete');
            });
        }

        result.sort((a, b) => {
            const aValue = a[sortConfig.field] || '';
            const bValue = b[sortConfig.field] || '';

            if (sortConfig.field === 'created_at' || sortConfig.field === 'due_date') {
                const dateA = new Date(aValue as string || 0).getTime();
                const dateB = new Date(bValue as string || 0).getTime();
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [allCards, searchQuery, sortConfig, hideDone]);

    const handleSort = (field: SortField) => {
        setSortConfig((current) => ({
            field,
            direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handleQuickAdd = async () => {
        if (!newTaskTitle.trim() || !selectedBoardId) return;

        setIsAdding(true);
        try {
            const board = boards.find((b) => b.id === selectedBoardId);
            if (!board || !board.columns || board.columns.length === 0) {
                throw new Error("Board has no columns");
            }

            const firstColumn = [...board.columns].sort((a, b) => a.order - b.order)[0];

            await api.post('/cards', {
                title:     newTaskTitle,
                column_id: firstColumn.id,
                status:    'todo',
            });

            setNewTaskTitle("");
            toast({ title: "Task added", description: `Added to ${board.title}` });
            onUpdate();
        } catch (error) {
            console.error("Error adding task:", error);
            toast({ title: "Error", description: "Failed to add task", variant: "destructive" });
        } finally {
            setIsAdding(false);
        }
    };

    const handleMarkDone = async (card: typeof allCards[0]) => {
        try {
            const board = boards.find((b) => b.id === card.boardId);
            if (!board) return;

            const doneColumn = board.columns?.find((col) =>
                col.title.toLowerCase().includes('done') ||
                col.title.toLowerCase().includes('complete')
            );

            if (!doneColumn) {
                toast({ title: "Cannot mark as done", description: "No 'Done' column found in this board", variant: "destructive" });
                return;
            }

            if (card.column_id === doneColumn.id) return;

            await api.patch(`/cards/${card.id}`, { column_id: doneColumn.id, status: 'done' });

            toast({ title: "Task completed", description: "Moved to Done" });
            onUpdate();
        } catch (error) {
            console.error("Error updating task:", error);
            toast({ title: "Error", description: "Failed to update task", variant: "destructive" });
        }
    };

    const handleDeleteDone = async () => {
        const doneCards = allCards.filter((card) => {
            const status = card.columnTitle.toLowerCase();
            return status.includes('done') || status.includes('complete');
        });

        if (doneCards.length === 0) {
            toast({ title: "Nothing to delete", description: "No completed tasks found" });
            return;
        }

        if (!window.confirm(`Delete ${doneCards.length} completed task${doneCards.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;

        try {
            await Promise.all(doneCards.map((card) => api.delete(`/cards/${card.id}`)));
            toast({ title: "Deleted", description: `Removed ${doneCards.length} completed task${doneCards.length !== 1 ? 's' : ''}` });
            onUpdate();
        } catch (error) {
            console.error("Error deleting done tasks:", error);
            toast({ title: "Error", description: "Failed to delete some tasks", variant: "destructive" });
        }
    };

    const openEditDialog = (card: Card) => {
        setSelectedCard(card);
        setIsEditDialogOpen(true);
    };

    const SortIcon = ({ field }: { field: SortField }) => {
        if (sortConfig.field !== field) return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
        return <ArrowUpDown className={`ml-2 h-4 w-4 ${sortConfig.direction === 'asc' ? 'rotate-180' : ''}`} />;
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Quick Add Section */}
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <Input
                            placeholder="What needs to be done?"
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleQuickAdd()}
                            className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-400"
                        />
                    </div>
                    <div className="w-full md:w-64">
                        <Select value={selectedBoardId} onValueChange={setSelectedBoardId}>
                            <SelectTrigger className="bg-white/[0.05] border-white/[0.1] text-white">
                                <SelectValue placeholder="Select Board" />
                            </SelectTrigger>
                            <SelectContent>
                                {boards.map((board) => (
                                    <SelectItem key={board.id} value={board.id}>
                                        {board.title}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        onClick={handleQuickAdd}
                        disabled={!newTaskTitle.trim() || !selectedBoardId || isAdding}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white min-w-[100px]"
                    >
                        {isAdding ? "Adding..." : "Add Task"}
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        placeholder="Filter tasks..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-white/[0.02] border-white/[0.05] text-white placeholder:text-slate-400"
                    />
                </div>
                <div className="flex items-center gap-2 bg-white/[0.02] border border-white/[0.05] rounded-lg px-3 py-2">
                    <Checkbox
                        id="hide-done"
                        checked={hideDone}
                        onCheckedChange={(checked) => setHideDone(checked as boolean)}
                        className="border-white/[0.2]"
                    />
                    <Label
                        htmlFor="hide-done"
                        className="text-sm text-slate-300 cursor-pointer select-none"
                    >
                        Hide Done
                    </Label>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteDone}
                    className="flex items-center gap-2 text-slate-400 hover:text-red-400 bg-white/[0.02] border border-white/[0.05] rounded-lg px-3 py-2 h-auto"
                >
                    <Trash2 className="h-4 w-4" />
                    <span className="text-sm">Delete Done</span>
                </Button>
            </div>

            {/* Tasks Table */}
            <div className="rounded-xl border border-white/[0.05] bg-white/[0.02] overflow-hidden">
                <Table>
                    <TableHeader className="bg-white/[0.02]">
                        <TableRow className="border-white/[0.05] hover:bg-transparent">
                            <TableHead className="w-[50px] text-slate-300">Check Status</TableHead>
                            <TableHead
                                className="text-slate-300 cursor-pointer hover:text-white"
                                onClick={() => handleSort('title')}
                            >
                                <div className="flex items-center">Task <SortIcon field="title" /></div>
                            </TableHead>
                            <TableHead
                                className="text-slate-300 cursor-pointer hover:text-white"
                                onClick={() => handleSort('boardTitle')}
                            >
                                <div className="flex items-center">Board <SortIcon field="boardTitle" /></div>
                            </TableHead>
                            <TableHead
                                className="text-slate-300 cursor-pointer hover:text-white"
                                onClick={() => handleSort('columnTitle')}
                            >
                                <div className="flex items-center">Status <SortIcon field="columnTitle" /></div>
                            </TableHead>
                            <TableHead
                                className="text-slate-300 cursor-pointer hover:text-white"
                                onClick={() => handleSort('due_date')}
                            >
                                <div className="flex items-center">Due Date <SortIcon field="due_date" /></div>
                            </TableHead>
                            <TableHead
                                className="text-slate-300 cursor-pointer hover:text-white"
                                onClick={() => handleSort('created_at')}
                            >
                                <div className="flex items-center">Created <SortIcon field="created_at" /></div>
                            </TableHead>
                            <TableHead className="text-right text-slate-300">Action</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedAndFilteredCards.length === 0 ? (
                            <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                                    No tasks found
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedAndFilteredCards.map((card) => {
                                const isDone = card.columnTitle.toLowerCase().includes('done') ||
                                    card.columnTitle.toLowerCase().includes('complete');

                                return (
                                    <TableRow key={card.id} className="border-white/[0.05] hover:bg-white/[0.02]">
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className={`h-8 w-8 ${isDone ? 'text-green-400' : 'text-slate-400 hover:text-indigo-400'}`}
                                                onClick={() => openEditDialog(card)}
                                            >
                                                {isDone ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                                            </Button>
                                        </TableCell>
                                        <TableCell className="font-medium text-slate-200">
                                            <span className={isDone ? "line-through text-slate-500" : ""}>
                                                {card.title}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="bg-white/[0.05] text-slate-300 border-white/[0.1]">
                                                {card.boardTitle}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant="secondary"
                                                className={`
                          ${isDone ? 'bg-green-500/10 text-green-400' : 'bg-indigo-500/10 text-indigo-400'}
                        `}
                                            >
                                                {card.columnTitle}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-slate-400 text-sm">
                                            {card.due_date ? (
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-3 w-3" />
                                                    {format(new Date(card.due_date + 'T00:00:00'), 'MMM d')}
                                                </div>
                                            ) : (
                                                <span className="text-slate-600">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-slate-400 text-sm">
                                            {card.created_at && formatDistanceToNow(new Date(card.created_at), { addSuffix: true })}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {!isDone && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleMarkDone(card)}
                                                    className="text-slate-400 hover:text-green-400"
                                                >
                                                    Mark Done
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {selectedCard && (
                <EditCardDialog
                    card={selectedCard}
                    isOpen={isEditDialogOpen}
                    onOpenChange={setIsEditDialogOpen}
                    onUpdate={onUpdate}
                />
            )}
        </div>
    );
}
